/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect } from 'react';
import {
  useForm, getFormProps, getFieldProps, getSubmitButtonProps, PlainTextEditor, FIND_ZICASSO_MAPPING, MultiSelectorAutocomplete,
} from '@marageti/z4-lib';
import {
  FormControl, RadioGroup, FormControlLabel, Radio,
} from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { LoadingButton } from '@mui/lab';
import { Controller } from 'react-hook-form';
import StepPage from '../step-page';
import { StepFormType } from '../types';

const schema = z.object({
  bestParts: z.string().min(1),
  doDifferently: z.string().optional(),
  nextCountry: z.string().optional(),
  nextDestinations: z.array(z.string()).optional(),
  findZicasso: z.string().optional(),
});

interface Step2Props extends StepFormType {
  destinationsList: any,
  destinationsListError: any,
  destinationsListIsLoading: boolean,
}

const Step2 = ({
  tripMatch,
  onBack,
  onNext,
  defaultValues,
  activeStep,
  error: formError,
  destinationsList,
  destinationsListError,
  destinationsListIsLoading,
}: Step2Props) => {
  const {
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    reset,
    control,
  } = useForm({
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues]);

  const { helperText: bestPartsHelperText } = getFieldProps('bestParts', errors);
  const { helperText: doDifferentlyHelperText } = getFieldProps('doDifferently', errors);
  const { helperText: nextDestinationsHelperText } = getFieldProps('nextDestinations', errors);

  if (!tripMatch) {
    return (
      <StepPage.Skeleton />
    );
  }

  return (
    <form {...getFormProps(handleSubmit(onNext))}>
      <StepPage
        subtitle={tripMatch.partnerName}
        activeStep={activeStep}
        steps={2}
        fields={[[
          'What were the most enjoyable or memorable parts of your trip?',
          <Controller
            control={control}
            name="bestParts"
            render={({ field }) => (
              <PlainTextEditor
                defaultValue={defaultValues.bestParts}
                editorName="bestParts"
                error={!!errors.bestParts}
                helperText={bestPartsHelperText}
                onChange={field.onChange}
                useLists
                sx={{
                  minHeight: { desktop: '135px' },
                  maxHeight: '135px',
                  flex: 1,
                }}
              />
            )}
          />,
        ],
        [
          'If you could take your trip again, what would you do differently? (Optional)',
          <Controller
            control={control}
            name="doDifferently"
            render={({ field }) => (
              <PlainTextEditor
                defaultValue={defaultValues.doDifferently}
                editorName="doDifferently"
                error={!!errors.doDifferently}
                helperText={doDifferentlyHelperText}
                onChange={field.onChange}
                useLists
                sx={{
                  minHeight: { desktop: '135px' },
                  maxHeight: '135px',
                  flex: 1,
                }}
              />
            )}
          />,
        ],
        ['Where are you thinking of traveling next? (Optional)',
          <Controller
            name="nextDestinations"
            control={control}
            render={({ field }) => {
              const getCurrentDestinationValue = (value: any) => {
                if (value && destinationsList) {
                  return destinationsList.filter((destination: any) => value.includes(destination.id));
                }
                return [];
              };

              return (
                <MultiSelectorAutocomplete
                  {...field}
                  label="Destination"
                  valueFieldName="id"
                  labelFieldName="name"
                  value={getCurrentDestinationValue(field.value)}
                  onChange={(_: any, data: any[]) => {
                    const ids = data.map((destination) => destination.id);
                    field.onChange(ids);
                  }}
                  isLoading={destinationsListIsLoading}
                  optionEntities={destinationsList}
                  helperText={destinationsListError ? 'Failed to load destinations' : nextDestinationsHelperText}
                  error={!!errors.nextDestinations || !!destinationsListError}
                />
              );
            }}
          />,
        ],
        [
          'How did you learn about Zicasso? (Optional)',
          <Controller
            control={control}
            name="findZicasso"
            defaultValue=""
            render={({ field: { onChange, value } }) => (
              <FormControl>
                <RadioGroup onChange={onChange} value={value}>
                  {Object.entries(FIND_ZICASSO_MAPPING).map(([key, label]) => (
                    <FormControlLabel
                      key={key}
                      value={key}
                      control={<Radio />}
                      label={label}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            )}
          />,
        ]]}
        error={formError}
        onBack={onBack}
        nextButton={(
          <LoadingButton
            type="submit"
            variant="contained"
            {...getSubmitButtonProps(isSubmitting, isValid)}
          >
            Next
          </LoadingButton>
        )}
      />
    </form>
  );
};

export default Step2;
